import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerPharmacy } from '../../../redux/middleware/user/pharmacy/registration';
import { pharmacyGoogleRegister } from '../../../redux/middleware/user/pharmacy/googleAuth';
import { pharmacyVerifyCaptcha } from '../../../redux/middleware/user/pharmacy/captcha';
import {
	openModal,
	closeModal,
} from '../../../redux/middleware/user/pharmacy/modal';
import ReCAPTCHA from 'react-google-recaptcha';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Eye, EyeOff } from 'react-feather';
import {
	CircularProgress,
	Modal,
	Box,
	Typography,
	Button,
} from '@mui/material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Link, useNavigate } from 'react-router-dom';

const PharmacyRegister = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const captchaRef = useRef(null);
	const siteKey = process.env.GOOGLE_RECAPTCH_SITE_KEY;
	const client_id = process.env.GOOGLE_CLIENT_ID;

	const { loading } = useSelector((state) => state.pharmacyCaptcha);
	const { successMessage, error } = useSelector(
		(state) => state.pharmacyRegister,
	);
	const { isModalOpen } = useSelector((state) => state.modal);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isFormValid, setIsFormValid] = useState(false);

	const [formData, setFormData] = useState({
		pharmacy_name: '',
		email: '',
		password: '',
		confirm_password: '',
		recaptcha_token: '',
		terms_accepted: false,
	});

	const [errors, setErrors] = useState({});
	const [passwordCriteria, setPasswordCriteria] = useState({
		length: false,
		number: false,
		special: false,
		uppercase: false,
		lowercase: false,
	});

	// Validate a single field
	const validateField = (name, value) => {
		switch (name) {
			case 'pharmacy_name':
				if (!value.trim()) {
					return 'Pharmacy name is required';
				} else if (value.length > 100) {
					return 'Pharmacy name should be less than 100 characters';
				}
				return '';

			case 'email':
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				if (!value.trim()) {
					return 'Email is required';
				} else if (!emailRegex.test(value)) {
					return 'Please enter a valid email address';
				}
				return '';

			case 'password':
				// We'll check password criteria separately
				return '';

			case 'confirm_password':
				if (value !== formData.password) {
					return 'Passwords do not match';
				}
				return '';

			case 'terms_accepted':
				if (!value) {
					return 'You must accept the terms and conditions';
				}
				return '';

			default:
				return '';
		}
	};

	// Check password criteria and update state
	const checkPasswordCriteria = (password) => {
		const newCriteria = {
			length: password.length >= 8,
			number: /\d/.test(password),
			special: /[!@#$%^&*]/.test(password),
			uppercase: /[A-Z]/.test(password),
			lowercase: /[a-z]/.test(password),
		};
		setPasswordCriteria(newCriteria);

		// Return an error message if any criteria is not met
		if (!newCriteria.length) {
			return 'Password must be at least 8 characters long';
		} else if (!newCriteria.number) {
			return 'Password must include at least one number';
		} else if (!newCriteria.special) {
			return 'Password must include at least one special character';
		} else if (!newCriteria.uppercase) {
			return 'Password must include at least one uppercase letter';
		} else if (!newCriteria.lowercase) {
			return 'Password must include at least one lowercase letter';
		}
		return '';
	};

	// Check if all password criteria are met
	const areAllPasswordCriteriaMet = () => {
		return Object.values(passwordCriteria).every(
			(criteria) => criteria === true,
		);
	};

	// Check if form is valid (all fields are filled and valid)
	const checkFormValidity = () => {
		// Check if all required fields are filled
		const requiredFieldsFilled =
			formData.pharmacy_name.trim() !== '' &&
			formData.email.trim() !== '' &&
			formData.password.trim() !== '' &&
			formData.confirm_password.trim() !== '';

		// Check if there are no errors
		const noErrors =
			Object.keys(errors).length === 0 ||
			Object.values(errors).every((error) => error === '');

		// Check if password criteria are met
		const passwordValid = areAllPasswordCriteriaMet();

		// Check if passwords match
		const passwordsMatch = formData.password === formData.confirm_password;

		// Check if terms are accepted and reCAPTCHA is verified
		const termsAndCaptcha = formData.terms_accepted && isCaptchaVerified;

		return (
			requiredFieldsFilled &&
			noErrors &&
			passwordValid &&
			passwordsMatch &&
			termsAndCaptcha
		);
	};

	// Update form validity whenever relevant states change
	useEffect(() => {
		setIsFormValid(checkFormValidity());
	}, [formData, errors, isCaptchaVerified, passwordCriteria]);

	const validateForm = () => {
		const newErrors = {};

		// Validate each field
		Object.keys(formData).forEach((field) => {
			if (field === 'password') {
				const passwordError = checkPasswordCriteria(formData.password);
				if (passwordError) newErrors.password = passwordError;
			} else {
				const error = validateField(field, formData[field]);
				if (error) newErrors[field] = error;
			}
		});

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		const newValue = type === 'checkbox' ? checked : value;

		setFormData((prevData) => ({
			...prevData,
			[name]: newValue,
		}));

		// Real-time validation
		if (name === 'password') {
			checkPasswordCriteria(value);

			// Also validate confirm password if it has a value
			if (formData.confirm_password) {
				const confirmError =
					value !== formData.confirm_password ? 'Passwords do not match' : '';
				setErrors((prev) => ({
					...prev,
					confirm_password: confirmError,
				}));
			}
		} else {
			const fieldError = validateField(name, newValue);
			setErrors((prev) => ({
				...prev,
				[name]: fieldError,
			}));
		}
	};

	const handleRecaptchaChange = async (token) => {
		setFormData({ ...formData, recaptcha_token: token });

		try {
			await dispatch(pharmacyVerifyCaptcha(token));
			setIsCaptchaVerified(true);
		} catch (error) {
			setIsCaptchaVerified(false);
			console.error('reCAPTCHA verification failed:', error);
		}
	};

	const resetCaptcha = () => {
		if (captchaRef.current) {
			captchaRef.current.reset();
			setIsCaptchaVerified(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm() || !isCaptchaVerified) {
			return;
		}

		setIsSubmitting(true);

		try {
			// Only send the required fields
			const submissionData = {
				pharmacy_name: formData.pharmacy_name,
				email: formData.email,
				password: formData.password,
				confirm_password: formData.confirm_password,
				terms_accepted: formData.terms_accepted,
				recaptcha_token: formData.recaptcha_token,
			};

			await dispatch(registerPharmacy(submissionData));
			dispatch(openModal());

			// Reset form after successful submission
			setFormData({
				pharmacy_name: '',
				email: '',
				password: '',
				confirm_password: '',
				recaptcha_token: '',
				terms_accepted: false,
			});
			resetCaptcha();
		} catch (error) {
			console.error('Registration Failed:', error);
			resetCaptcha();
		} finally {
			setIsSubmitting(false);
		}
	};

	const handlepharmacyGoogleRegister = (response) => {
		if (response.error) {
			console.error('Google login failed', response.error);
			return;
		}

		const tokenId = response.tokenId; // Google OAuth token
		dispatch(pharmacyGoogleRegister(tokenId));
	};

	const handleOkClick = () => {
		dispatch(closeModal());
	};

	return (
		<div className={css(styles.container)}>
			<form onSubmit={handleSubmit} className={css(styles.form)}>
				<motion.div
					className={css(styles.inputGroup)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.1 }}
				>
					<input
						type="text"
						name="pharmacy_name"
						placeholder="Pharmacy Name"
						value={formData.pharmacy_name}
						onChange={handleChange}
						className={css(styles.input)}
					/>
					{errors.pharmacy_name && (
						<p className={css(styles.error)}>{errors.pharmacy_name}</p>
					)}
				</motion.div>

				<motion.div
					className={css(styles.inputGroup)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.3 }}
				>
					<input
						type="email"
						name="email"
						placeholder="Email"
						value={formData.email}
						onChange={handleChange}
						className={css(styles.input)}
					/>
					{errors.email && <p className={css(styles.error)}>{errors.email}</p>}
				</motion.div>

				<motion.div
					className={css(styles.inputGroup)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.5 }}
				>
					<div className={css(styles.passwordInputWrapper)}>
						<input
							type={showPassword ? 'text' : 'password'}
							name="password"
							placeholder="Password"
							value={formData.password}
							onChange={handleChange}
							className={css(styles.input, styles.passwordInput)}
						/>
						<button
							type="button"
							onClick={() => setShowPassword(!showPassword)}
							className={css(styles.passwordToggle)}
						>
							{showPassword ? (
								<EyeOff className={css(styles.eyeIcon)} />
							) : (
								<Eye className={css(styles.eyeIcon)} />
							)}
						</button>
					</div>
					{errors.password && (
						<p className={css(styles.error)}>{errors.password}</p>
					)}

					{/* Password requirements section */}
					{formData.password.length > 0 && (
						<div className={css(styles.passwordRequirements)}>
							<p className={css(styles.requirementsTitle)}>
								Password must have:
							</p>
							<ul className={css(styles.requirementsList)}>
								<li
									className={css(
										passwordCriteria.length
											? styles.metRequirement
											: styles.unmetRequirement,
									)}
								>
									At least 8 characters
								</li>
								<li
									className={css(
										passwordCriteria.uppercase
											? styles.metRequirement
											: styles.unmetRequirement,
									)}
								>
									At least one uppercase letter (A-Z)
								</li>
								<li
									className={css(
										passwordCriteria.lowercase
											? styles.metRequirement
											: styles.unmetRequirement,
									)}
								>
									At least one lowercase letter (a-z)
								</li>
								<li
									className={css(
										passwordCriteria.number
											? styles.metRequirement
											: styles.unmetRequirement,
									)}
								>
									At least one number (0-9)
								</li>
								<li
									className={css(
										passwordCriteria.special
											? styles.metRequirement
											: styles.unmetRequirement,
									)}
								>
									At least one special character (!@#$%^&*)
								</li>
							</ul>
						</div>
					)}
				</motion.div>

				<motion.div
					className={css(styles.inputGroup)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.6 }}
				>
					<div className={css(styles.passwordInputWrapper)}>
						<input
							type={showConfirmPassword ? 'text' : 'password'}
							name="confirm_password"
							placeholder="Confirm Password"
							value={formData.confirm_password}
							onChange={handleChange}
							className={css(styles.input, styles.passwordInput)}
						/>
						<button
							type="button"
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							className={css(styles.passwordToggle)}
						>
							{showConfirmPassword ? (
								<EyeOff className={css(styles.eyeIcon)} />
							) : (
								<Eye className={css(styles.eyeIcon)} />
							)}
						</button>
					</div>
					{errors.confirm_password && (
						<p className={css(styles.error)}>{errors.confirm_password}</p>
					)}
				</motion.div>

				<motion.label
					className={css(styles.checkboxLabel)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.7 }}
				>
					<input
						type="checkbox"
						name="terms_accepted"
						checked={formData.terms_accepted}
						onChange={handleChange}
					/>
					I agree to the Terms and Privacy Policy
					{errors.terms_accepted && (
						<p className={css(styles.error)}>{errors.terms_accepted}</p>
					)}
				</motion.label>

				<motion.div
					className={css(styles.captcha)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.8 }}
				>
					<ReCAPTCHA
						ref={captchaRef}
						sitekey={siteKey}
						onChange={handleRecaptchaChange}
					/>
				</motion.div>

				<motion.button
					type="submit"
					className={css(styles.button)}
					disabled={isSubmitting || !isFormValid}
					whileHover={isSubmitting || !isFormValid ? {} : { scale: 1.05 }}
					whileTap={isSubmitting || !isFormValid ? {} : { scale: 0.95 }}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.9 }}
				>
					{isSubmitting ? (
						<CircularProgress size={24} color="inherit" />
					) : (
						'Register'
					)}
				</motion.button>

				<div className={css(styles.divider)}>
					<span>Or</span>
				</div>
				<motion.div
					whileHover={{ scale: 1.05 }}
					whileTap={{ scale: 0.95 }}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 1.0 }}
				>
					<GoogleOAuthProvider clientId={client_id}>
						<GoogleLogin
							onSuccess={handlepharmacyGoogleRegister}
							onError={(error) => console.error('Google Login Error:', error)}
							text="signup_with"
							logo_alignment="center"
						/>
					</GoogleOAuthProvider>
				</motion.div>
			</form>

			{/* Updated Success Modal */}
			<Modal
				open={isModalOpen}
				onClose={handleOkClick}
				aria-labelledby="modal-title"
				aria-describedby="modal-description"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'white',
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						width: 400,
						textAlign: 'center',
					}}
				>
					<Typography
						id="modal-title"
						variant="h6"
						component="h2"
						sx={{ color: '#2783F7' }}
					>
						Registration Successful
					</Typography>
					<Typography id="modal-description" sx={{ mt: 2, color: '#4a5568' }}>
						A verification email has been sent to your inbox. Please verify your
						account to continue. If you don't see it, please check your spam
						folder.
					</Typography>

					<Button
						variant="contained"
						onClick={handleOkClick}
						sx={{
							mt: 3,
							bgcolor: '#2783F7',
							'&:hover': { bgcolor: '#1c5fa8' },
						}}
					>
						Ok
					</Button>
				</Box>
			</Modal>
		</div>
	);
};

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		width: '100%',
	},
	inputGroup: {
		position: 'relative',
		marginBottom: '0.5rem',
	},
	input: {
		width: '100%',
		padding: '0.75rem',
		borderRadius: '8px',
		border: '1px solid #e2e8f0',
		fontSize: '1rem',
		transition: 'border-color 0.3s',
		':focus': {
			outline: 'none',
			borderColor: '#2783F7',
			boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)',
		},
	},
	passwordInputWrapper: {
		position: 'relative',
		width: '100%',
	},
	passwordInput: {
		paddingRight: '40px',
	},
	passwordToggle: {
		position: 'absolute',
		right: '10px',
		top: '50%',
		transform: 'translateY(-50%)',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: '#4a5568',
	},
	eyeIcon: {
		color: 'black',
		width: '1.15rem',
		height: '1.15rem',
	},
	error: {
		color: '#e53e3e',
		fontSize: '0.875rem',
		marginTop: '0.25rem',
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '0.5rem',
		fontSize: '0.875rem',
		color: '#4a5568',
		position: 'relative',
		paddingBottom: '1.5rem',
	},
	captcha: {
		display: 'flex',
		justifyContent: 'start',
		marginBottom: '1rem',
	},
	button: {
		backgroundColor: '#2783F7',
		color: 'white',
		padding: '0.75rem',
		borderRadius: '8px',
		border: 'none',
		fontSize: '1rem',
		fontWeight: '600',
		cursor: 'pointer',
		transition: 'background-color 0.3s',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '48px',
		':hover': {
			backgroundColor: '#1c5fa8',
		},
		':disabled': {
			backgroundColor: '#a0aec0',
			cursor: 'not-allowed',
		},
	},
	divider: {
		display: 'flex',
		alignItems: 'center',
		textAlign: 'center',
		margin: '1rem 0',
		color: '#718096',
		':before': {
			content: '""',
			flexGrow: 1,
			backgroundColor: '#e2e8f0',
			height: '1px',
			marginRight: '0.5rem',
		},
		':after': {
			content: '""',
			flexGrow: 1,
			backgroundColor: '#e2e8f0',
			height: '1px',
			marginLeft: '0.5rem',
		},
	},
	// Password requirements styling
	passwordRequirements: {
		marginTop: '0.5rem',
		padding: '0.75rem',
		borderRadius: '6px',
		backgroundColor: '#f8fafc',
		border: '1px solid #e2e8f0',
	},
	requirementsTitle: {
		fontSize: '0.85rem',
		fontWeight: '600',
		color: '#4a5568',
		marginBottom: '0.5rem',
	},
	requirementsList: {
		listStyleType: 'none',
		padding: 0,
		margin: 0,
	},
	metRequirement: {
		color: '#38a169',
		fontSize: '0.8rem',
		marginBottom: '0.25rem',
		display: 'flex',
		alignItems: 'center',
		':before': {
			content: '"✓"',
			marginRight: '0.5rem',
			fontWeight: 'bold',
		},
	},
	unmetRequirement: {
		color: '#a0aec0',
		fontSize: '0.8rem',
		marginBottom: '0.25rem',
		display: 'flex',
		alignItems: 'center',
		':before': {
			content: '"○"',
			marginRight: '0.5rem',
		},
	},
	googleButtonContainer: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
	},
	loginLink: {
		textAlign: 'center',
		fontSize: '0.875rem',
		color: '#4a5568',
		marginTop: '1rem',
	},
	link: {
		color: '#2783F7',
		textDecoration: 'none',
		fontWeight: '600',
		':hover': {
			textDecoration: 'underline',
		},
	},
});

export default PharmacyRegister;
